/**
 * Aggregation service for MetricData
 * Supports 8 aggregation combinations:
 * 1. None (no aggregation)
 * 2. By Date only
 * 3. By Day Part only
 * 4. By Dining Option only
 * 5. By Date + Day Part
 * 6. By Date + Dining Option
 * 7. By Day Part + Dining Option
 * 8. By Date + Day Part + Dining Option (all three)
 *
 * @module MetricDataDevTool/services/aggregationService
 */

/**
 * Generate aggregation key based on configuration and data item
 * @param {Object} item - Data item
 * @param {Object} config - Aggregation configuration
 * @param {boolean} config.byDate - Aggregate by date
 * @param {boolean} config.byDayPart - Aggregate by day part
 * @param {boolean} config.byDiningOption - Aggregate by dining option
 * @returns {string} Aggregation key
 */
function generateAggregationKey(item, config) {
  const parts = [];

  if (config.byDate && item.date) {
    parts.push(`date:${item.date}`);
  }

  if (config.byDayPart) {
    parts.push(`dayPart:${item.dayPart || 'all'}`);
  }

  if (config.byDiningOption) {
    parts.push(`diningOption:${item.diningOption || 'all'}`);
  }

  // Always include metricName in the key
  parts.push(`metric:${item.metricName}`);

  return parts.join('|');
}

/**
 * Extract dimensions from aggregation key
 * @param {string} key - Aggregation key
 * @param {Object} config - Aggregation configuration
 * @returns {Object} Dimensions object
 */
function extractDimensionsFromKey(key, config) {
  const dimensions = {};
  const parts = key.split('|');

  parts.forEach(part => {
    const [dimension, value] = part.split(':');
    if (dimension === 'date' && config.byDate) {
      dimensions.date = value;
    } else if (dimension === 'dayPart' && config.byDayPart) {
      dimensions.dayPart = value;
    } else if (dimension === 'diningOption' && config.byDiningOption) {
      dimensions.diningOption = value;
    } else if (dimension === 'metric') {
      dimensions.metricName = value;
    }
  });

  return dimensions;
}

/**
 * Aggregate metric data based on configuration
 * @param {Array<Object>} data - Array of metric data items
 * @param {Object} config - Aggregation configuration
 * @param {boolean} config.byDate - Aggregate by date
 * @param {boolean} config.byDayPart - Aggregate by day part
 * @param {boolean} config.byDiningOption - Aggregate by dining option
 * @returns {Array<Object>} Aggregated data
 */
export function aggregateMetricData(data, config = {}) {
  // If no aggregation is enabled, return original data sorted by metricName
  if (!config.byDate && !config.byDayPart && !config.byDiningOption) {
    return [...data].sort((a, b) =>
      (a.metricName || '').localeCompare(b.metricName || '')
    );
  }

  const aggregated = {};

  data.forEach(item => {
    const key = generateAggregationKey(item, config);

    if (!aggregated[key]) {
      const dimensions = extractDimensionsFromKey(key, config);
      aggregated[key] = {
        ...dimensions,
        numberValue: 0,
        recordCount: 0
      };
    }

    // Sum the numberValue
    aggregated[key].numberValue += item.numberValue || 0;
    aggregated[key].recordCount += 1;
  });

  // Convert to array and sort
  const result = Object.values(aggregated).map(item => ({
    ...item,
    numberValue: parseFloat(item.numberValue.toFixed(2))
  }));

  // Sort by dimensions in order: date, metricName, dayPart, diningOption
  return result.sort((a, b) => {
    if (config.byDate && a.date !== b.date) {
      return (a.date || '').localeCompare(b.date || '');
    }
    if (a.metricName !== b.metricName) {
      return (a.metricName || '').localeCompare(b.metricName || '');
    }
    if (config.byDayPart && a.dayPart !== b.dayPart) {
      return (a.dayPart || '').localeCompare(b.dayPart || '');
    }
    if (config.byDiningOption && a.diningOption !== b.diningOption) {
      return (a.diningOption || '').localeCompare(b.diningOption || '');
    }
    return 0;
  });
}

/**
 * Get column definitions based on aggregation configuration
 * @param {Object} config - Aggregation configuration
 * @param {boolean} config.byDate - Aggregate by date
 * @param {boolean} config.byDayPart - Aggregate by day part
 * @param {boolean} config.byDiningOption - Aggregate by dining option
 * @param {string[]} selectedMetrics - Selected metric names
 * @returns {Array<Object>} Column definitions for table
 */
export function getAggregationColumns(config, selectedMetrics) {
  const columns = [];

  // Add dimension columns based on config
  if (config.byDate) {
    columns.push({
      id: 'date',
      header: 'Date',
      accessorKey: 'date'
    });
  }

  if (config.byDayPart) {
    columns.push({
      id: 'dayPart',
      header: 'Day Part',
      accessorKey: 'dayPart'
    });
  }

  if (config.byDiningOption) {
    columns.push({
      id: 'diningOption',
      header: 'Dining Option',
      accessorKey: 'diningOption'
    });
  }

  // Add metric column (or multiple if not aggregating)
  if (!config.byDate && !config.byDayPart && !config.byDiningOption) {
    // No aggregation - show metricName column
    columns.push({
      id: 'metricName',
      header: 'Metric Name',
      accessorKey: 'metricName'
    });
  }

  // Add value column(s)
  columns.push({
    id: 'numberValue',
    header: 'Value',
    accessorKey: 'numberValue',
    align: 'right'
  });

  // Add record count column for aggregated data
  if (config.byDate || config.byDayPart || config.byDiningOption) {
    columns.push({
      id: 'recordCount',
      header: 'Record Count',
      accessorKey: 'recordCount',
      align: 'right'
    });
  }

  return columns;
}

/**
 * Get aggregation summary description
 * @param {Object} config - Aggregation configuration
 * @returns {string} Human-readable aggregation description
 */
export function getAggregationDescription(config) {
  const parts = [];

  if (config.byDate) parts.push('Date');
  if (config.byDayPart) parts.push('Day Part');
  if (config.byDiningOption) parts.push('Dining Option');

  if (parts.length === 0) {
    return 'No aggregation';
  }

  return `Aggregated by ${parts.join(' + ')}`;
}
